/**
 * Module 00 — Global Shell · Icon registry
 *
 * The prototype ships a bespoke inline-SVG `Icon` registry (`icons.jsx`). The
 * production app standardises on `lucide-react` (already used across the
 * codebase), so this module maps the prototype's string icon names onto Lucide
 * components. `<Icon name=… />` stays the call-site API the shell expects, and
 * unknown names fall back to a neutral glyph (never crash).
 */

'use client';

import * as React from 'react';
import {
  Home, Rocket, History, Cpu, Plug, Server, Bot, Workflow, ListChecks,
  Sparkles, LayoutGrid, BookOpen, Network, Boxes, Database, Table2,
  ShieldCheck, Zap, ArrowLeftRight, FlaskConical, GitPullRequest, PlayCircle,
  Download, FileSearch, BadgeCheck, BarChart3, Building2, Users,
  GitBranch, FileCog, BookMarked, Gauge, CreditCard, Search, ChevronDown,
  ChevronUp, ChevronRight, ChevronLeft, Crown, Sun, Moon, HelpCircle, Check,
  Copy, Eye, RefreshCw, Lock, Lightbulb, MessageSquare, Terminal, Pencil,
  CheckCircle2, SlidersHorizontal, Plus, Send, MoreHorizontal, ArrowRight,
  Globe, Info, AlertTriangle, Wrench, Mail, Folder, Mic, Github, Trash2,
  X, FileText, List, Target, Wand2, Link2, Filter, LogOut, LibraryBig, GitFork,
  Activity, Maximize2, Minimize2, Minus, Scan, Shield,
  LayoutDashboard, ScrollText, Gavel, FolderLock, Scale, ClipboardCheck,
  ListFilter, ToggleRight, UserCheck, KeyRound, Shapes, Cable, Package,
  MousePointerClick, FileCheck, Landmark, Store, PackageCheck,
  type LucideIcon,
} from 'lucide-react';

const REGISTRY: Record<string, LucideIcon> = {
  // nav glyphs
  home: Home,
  rocket: Rocket,
  history: History,
  models: Cpu,
  plug: Plug,
  mcp: Server,
  agent: Bot,
  processflow: Workflow,
  executions: ListChecks,
  skills: Sparkles,
  cards: LayoutGrid,
  fullscreen: Maximize2,
  exitfullscreen: Minimize2,
  minus: Minus,
  learn: BookOpen,
  knowledgegraph: Network,
  cognitive: Boxes,
  kb: Database,
  grid: Table2,
  policy: ListFilter,
  zap: Zap,
  mappings: ArrowLeftRight,
  flask: FlaskConical,
  approval: GitPullRequest,
  simulation: PlayCircle,
  play: PlayCircle,
  trash: Trash2,
  ingestion: Download,
  download: Download,
  // FAB-330: every sidebar item gets its own glyph, so the collapsed rail can
  // be told apart without hovering. Keys also used outside the nav list (lock,
  // plug, zap, approval, cognitive, org) keep their glyph; the nav points at
  // the dedicated keys below instead.
  govoverview: LayoutDashboard,
  principles: ScrollText,
  aipolicy: Gavel,
  datapolicy: FolderLock,
  clearance: BadgeCheck,
  guardrails: ShieldCheck,
  responsible: Scale,
  observability: Activity,
  audit: FileSearch,
  compliance: ClipboardCheck,
  ontology: Shapes,
  actions: MousePointerClick,
  connsim: Cable,
  skilllibrary: Package,
  skillreview: FileCheck,
  grants: UserCheck,
  usagecontrols: ToggleRight,
  apikeys: KeyRound,
  // Settings → Org; the org button at the top of the rail keeps `org`.
  orgsettings: Landmark,
  analytics: BarChart3,
  // Apps group (app marketplace)
  appstore: Store,
  installedapps: PackageCheck,
  org: Building2,
  users: Users,
  roles: Shield,
  departments: GitBranch,
  projects: Boxes,
  constitution: FileCog,
  usage: Gauge,
  billing: CreditCard,
  // chrome / palette / simple-mode glyphs
  search: Search,
  chevdown: ChevronDown,
  chevup: ChevronUp,
  chevright: ChevronRight,
  chevleft: ChevronLeft,
  crown: Crown,
  sun: Sun,
  moon: Moon,
  help: HelpCircle,
  check: Check,
  copy: Copy,
  eye: Eye,
  refresh: RefreshCw,
  lock: Lock,
  bulb: Lightbulb,
  message: MessageSquare,
  terminal: Terminal,
  edit: Pencil,
  checkcircle: CheckCircle2,
  sliders: SlidersHorizontal,
  logout: LogOut,
  sparkles: Sparkles,
  // Kebab / overflow menu trigger. Without this entry `name="dots"` fell through
  // to the Sparkles fallback and rendered as a star on the connector cards.
  dots: MoreHorizontal,
  book: BookMarked,
  // context-graph module glyphs
  plus: Plus,
  x: X,
  arrowright: ArrowRight,
  filetext: FileText,
  list: List,
  target: Target,
  wand: Wand2,
  info: Info,
  alert: AlertTriangle,
  database: Database,
  link: Link2,
  filter: Filter,
  maximize: Maximize2,
  minimize: Minimize2,
  fit: Scan,
  // Data Catalog module
  catalog: LibraryBig,
  lineage: GitFork,
};

export interface IconProps {
  name: string;
  size?: number;
  className?: string;
  style?: React.CSSProperties;
  'aria-hidden'?: boolean;
}

export function Icon({ name, size = 18, className, ...rest }: IconProps) {
  const Cmp = REGISTRY[name] || Sparkles;
  return <Cmp size={size} className={className} aria-hidden {...rest} />;
}

export function hasIcon(name: string): boolean {
  return name in REGISTRY;
}
